/**
 * File Viewer capability detection (ADR-052).
 * Server MIME is authoritative; extension is fallback only.
 */

export const FILE_VIEWER_UNSUPPORTED_TOOLTIP = 'This file type cannot be previewed. Download the file instead.';

export const FILE_VIEWER_ERROR_MESSAGE = 'This file could not be previewed.';

export const FILE_VIEWER_DOCUMENT_PENDING_MESSAGE =
    'Document preview for this file type will be available after the viewer library is selected. You can download the file in the meantime.';

/** Product copy for Office previews converted client-side (Word, Excel, etc.). */
export const FILE_VIEWER_PREVIEW_DISCLAIMER = 'Preview only. Formatting may differ from the original file.';

/**
 * MIME types whose in-app preview is known to differ from native desktop viewers.
 * Extend this list when additional document renderers are added.
 */
export const FILE_VIEWER_PREVIEW_DISCLAIMER_MIME_TYPES = Object.freeze([
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
]);

/**
 * @param {string|null|undefined} mimeType
 * @returns {boolean}
 */
export function fileViewerShowsPreviewDisclaimer(mimeType) {
    if (!mimeType || typeof mimeType !== 'string') {
        return false;
    }

    return FILE_VIEWER_PREVIEW_DISCLAIMER_MIME_TYPES.includes(mimeType.toLowerCase().split(';')[0].trim());
}

export const FILE_VIEWER_USE_CASES = Object.freeze(['activity', 'grader', 'submission']);

export const FILE_VIEWER_DRAWER_SETTING_PREFIX = 'file_viewer_drawer_state';

/** @param {'activity'|'grader'|'submission'} useCase */
export function fileViewerDrawerSettingKey(useCase) {
    return `${FILE_VIEWER_DRAWER_SETTING_PREFIX}.${useCase}`;
}

const RENDERERS = Object.freeze({
    DOCUMENT: 'document',
    IMAGE: 'image',
    VIDEO: 'video',
    AUDIO: 'audio',
    UNSUPPORTED: 'unsupported',
});

/** @type {Record<string, { renderer: string, extensions: string[] }>} */
const MIME_TABLE = {
    'application/pdf': { renderer: RENDERERS.DOCUMENT, extensions: ['.pdf'] },
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': {
        renderer: RENDERERS.DOCUMENT,
        extensions: ['.docx'],
    },
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': {
        renderer: RENDERERS.DOCUMENT,
        extensions: ['.xlsx'],
    },
    // PPTX stays unsupported until the renderer spike passes (ADR-052).
    'image/jpeg': { renderer: RENDERERS.IMAGE, extensions: ['.jpg', '.jpeg'] },
    'image/png': { renderer: RENDERERS.IMAGE, extensions: ['.png'] },
    'image/gif': { renderer: RENDERERS.IMAGE, extensions: ['.gif'] },
    'image/svg+xml': { renderer: RENDERERS.IMAGE, extensions: ['.svg'] },
    'video/mp4': { renderer: RENDERERS.VIDEO, extensions: ['.mp4'] },
    'video/webm': { renderer: RENDERERS.VIDEO, extensions: ['.webm'] },
    'audio/mpeg': { renderer: RENDERERS.AUDIO, extensions: ['.mp3'] },
    'audio/wav': { renderer: RENDERERS.AUDIO, extensions: ['.wav'] },
    'audio/x-wav': { renderer: RENDERERS.AUDIO, extensions: ['.wav'] },
};

const EXTENSION_TO_MIME = Object.freeze(
    Object.entries(MIME_TABLE).reduce((acc, [mime, meta]) => {
        meta.extensions.forEach(ext => {
            if (!acc[ext]) {
                acc[ext] = mime;
            }
        });
        return acc;
    }, {})
);

/**
 * @param {object|File|null|undefined} file
 * @returns {string}
 */
export function resolveFileViewerFileName(file) {
    if (!file) {
        return 'File';
    }

    return file.name || file.title || file.originalName || file.file_name || file.fileName || 'File';
}

/**
 * @param {object|File|null|undefined} file
 * @returns {string|null}
 */
function resolveServerMime(file) {
    if (!file) {
        return null;
    }

    const mime = file.mime_type || file.mimeType || file.type || null;
    if (!mime || typeof mime !== 'string') {
        return null;
    }

    return mime.toLowerCase().split(';')[0].trim() || null;
}

/**
 * @param {object|File|null|undefined} file
 * @returns {string|null}
 */
function resolveExtension(file) {
    const name = resolveFileViewerFileName(file);
    const dot = name.lastIndexOf('.');
    if (dot === -1 || name === 'File') {
        return null;
    }

    return name.slice(dot).toLowerCase();
}

/**
 * @param {object|File|null|undefined} file
 * @returns {{ supported: boolean, renderer: string, mimeType: string }}
 */
export function getFileViewerCapability(file) {
    const serverMime = resolveServerMime(file);
    const extension = resolveExtension(file);

    let mimeType = serverMime;
    let renderer = MIME_TABLE[serverMime]?.renderer ?? null;

    // Prefer server MIME; fall back to extension only when MIME is missing or unknown.
    if (!renderer && extension) {
        const fromExt = EXTENSION_TO_MIME[extension];
        if (fromExt) {
            mimeType = fromExt;
            renderer = MIME_TABLE[fromExt].renderer;
        }
    }

    if (!renderer) {
        return {
            supported: false,
            renderer: RENDERERS.UNSUPPORTED,
            mimeType: mimeType || '',
        };
    }

    return {
        supported: true,
        renderer,
        mimeType: mimeType || '',
    };
}

export const FILE_VIEWER_RENDERERS = RENDERERS;
